/**
 * @fileoverview Types for Brex integration
 * Partner authentication: OAuth2 Authorization Code Grant
 * API docs: https://developer.brex.com/openapi/payments_api/
 */

import * as yup from 'yup'

// AUTH --------------------------------------------------------------------------------------------

export enum BrexAuthScopes {
  OpenId = 'openid',
  OfflineAccess = 'offline_access',
  IncomingTransfers = 'incoming_transfers',
  LinkedAccountsReadonly = 'linked_accounts.readonly',
  Transfers = 'transfers',
  TransfersReadonly = 'transfers.readonly',
  Vendors = 'vendors',
  VendorsReadonly = 'vendors.readonly',
}

export const brexTokenResponseSchema = yup.object({
  access_token: yup.string().required(),
  token_type: yup.string().required(),
  expires_in: yup.number().required(),
  scope: yup.string().optional(),
  refresh_token: yup.string().optional(),
  id_token: yup.string().optional(),
})

export type BrexTokenResponse = yup.InferType<typeof brexTokenResponseSchema>

export const brexAuthorizationResponseSchema = yup.object({
  code: yup.string().required(),
  state: yup.string().required(),
})

export type BrexAuthorizationResponse = yup.InferType<typeof brexAuthorizationResponseSchema>

// VENDORS -----------------------------------------------------------------------------------------

export const brexBeneficiaryAddressSchema = yup.object({
  line1: yup.string().nullable().notRequired(),
  line2: yup.string().nullable().notRequired(),
  city: yup.string().nullable().notRequired(),
  state: yup.string().nullable().notRequired(),
  country: yup.string().nullable().notRequired(),
  postal_code: yup.string().nullable().notRequired(),
  phone_number: yup.string().nullable().notRequired(),
})

/**
 * Flat combined schema for all payment account detail types (ACH, DOMESTIC_WIRE, CHEQUE).
 * The `type` field acts as a discriminator.
 *
 * ACH required: type, routing_number, account_number, account_type, account_class
 * DOMESTIC_WIRE required: type, routing_number, account_number, address
 * CHEQUE required: type, mailing_address, recipient_name
 */
export const brexPaymentAccountDetailsSchema = yup.object({
  type: yup.string().oneOf(['ACH', 'DOMESTIC_WIRE', 'CHEQUE']).required(),
  // ACH + DOMESTIC_WIRE
  routing_number: yup.string().notRequired(),
  account_number: yup.string().notRequired(),
  // ACH only
  account_type: yup.string().oneOf(['CHECKING', 'SAVING']).nullable().notRequired(),
  account_class: yup.string().oneOf(['BUSINESS', 'PERSONAL']).nullable().notRequired(),
  // DOMESTIC_WIRE only
  address: brexBeneficiaryAddressSchema.nullable().notRequired(),
  // CHEQUE only
  mailing_address: brexBeneficiaryAddressSchema.nullable().notRequired(),
  recipient_name: yup.string().nullable().notRequired(),
  // All types
  beneficiary_name: yup.string().nullable().notRequired(),
})

export const brexPaymentAccountSchema = yup.object({
  details: brexPaymentAccountDetailsSchema.required(),
})

export type BrexPaymentAccountDetails = yup.InferType<typeof brexPaymentAccountDetailsSchema>
export type BrexPaymentAccount = yup.InferType<typeof brexPaymentAccountSchema>

export const brexVendorCreateInputSchema = yup.object({
  company_name: yup.string().required(),
  email: yup.string().nullable().notRequired(),
  phone: yup.string().nullable().notRequired(),
  payment_accounts: yup.array(brexPaymentAccountSchema).nullable().notRequired(),
})

export type BrexVendorCreateInput = yup.InferType<typeof brexVendorCreateInputSchema>

export const brexVendorUpdateInputSchema = yup.object({
  company_name: yup.string().notRequired(),
  email: yup.string().nullable().notRequired(),
  phone: yup.string().nullable().notRequired(),
  payment_accounts: yup.array(brexPaymentAccountSchema).nullable().notRequired(),
  beneficiary_name: yup.string().nullable().notRequired(),
})

export type BrexVendorUpdateInput = yup.InferType<typeof brexVendorUpdateInputSchema>

export const brexVendorResponseSchema = yup.object({
  id: yup.string().required(),
  company_name: yup.string().notRequired(),
  email: yup.string().nullable().notRequired(),
  phone: yup.string().nullable().notRequired(),
  payment_accounts: yup.array(brexPaymentAccountSchema).nullable().notRequired(),
  beneficiary_address: brexBeneficiaryAddressSchema.nullable().notRequired(),
  created_at: yup.string().nullable().notRequired(),
  updated_at: yup.string().nullable().notRequired(),
})

export type BrexVendorResponse = yup.InferType<typeof brexVendorResponseSchema>

// INCOMING TRANSFERS ------------------------------------------------------------------------------

export const brexIncomingTransferInputSchema = yup.object({
  counterparty: yup
    .object({
      type: yup.string().oneOf(['BANK']).required(),
      id: yup.string().required(),
    })
    .required(),
  receiving_account: yup
    .object({
      type: yup.string().oneOf(['BREX_CASH']).required(),
      id: yup.string().required(),
    })
    .required(),
  amount: yup
    .object({
      amount: yup.number().integer().required(),
      currency: yup.string().nullable().notRequired(),
    })
    .required(),
  description: yup.string().required(),
})

export type BrexIncomingTransferInput = yup.InferType<typeof brexIncomingTransferInputSchema>

export const brexIncomingTransferResponseSchema = yup.object({
  id: yup.string().required(),
  counterparty: yup
    .object({
      type: yup.string().notRequired(),
      payment_instrument_id: yup.string().notRequired(),
      id: yup.string().notRequired(),
      routing_number: yup.string().notRequired(),
      account_number: yup.string().notRequired(),
    })
    .notRequired(),
  description: yup.string().notRequired(),
  payment_type: yup.string().notRequired(),
  amount: yup
    .object({
      amount: yup.number().notRequired(),
      currency: yup.string().nullable().notRequired(),
    })
    .notRequired(),
  process_date: yup.string().nullable().notRequired(),
  originating_account: yup
    .object({
      type: yup.string().notRequired(),
      id: yup.string().notRequired(),
    })
    .notRequired(),
  status: yup.string().notRequired(),
  cancellation_reason: yup.string().nullable().notRequired(),
  estimated_delivery_date: yup.string().nullable().notRequired(),
  creator_user_id: yup.string().nullable().notRequired(),
  created_at: yup.string().nullable().notRequired(),
  display_name: yup.string().nullable().notRequired(),
  external_memo: yup.string().nullable().notRequired(),
  is_ppro_enabled: yup.boolean().nullable().notRequired(),
})

export type BrexIncomingTransferResponse = yup.InferType<typeof brexIncomingTransferResponseSchema>

export const brexGetTransferResponseSchema = yup.object({
  id: yup.string().required(),
  counterparty: yup
    .object({
      type: yup.string().notRequired(),
      routing_number: yup.string().nullable().notRequired(),
      account_number: yup.string().nullable().notRequired(),
      description: yup.string().nullable().notRequired(),
      beneficiary_bank: yup
        .object({
          name: yup.string().nullable().notRequired(),
          address: yup
            .object({
              line1: yup.string().nullable().notRequired(),
              line2: yup.string().nullable().notRequired(),
              city: yup.string().nullable().notRequired(),
              state: yup.string().nullable().notRequired(),
              country: yup.string().nullable().notRequired(),
              postal_code: yup.string().nullable().notRequired(),
              phone_number: yup.string().nullable().notRequired(),
            })
            .nullable()
            .notRequired(),
        })
        .nullable()
        .notRequired(),
      fed_reference_number: yup.string().nullable().notRequired(),
      external_memo: yup.string().nullable().notRequired(),
    })
    .nullable()
    .notRequired(),
  description: yup.string().nullable().notRequired(),
  payment_type: yup
    .string()
    .oneOf(['ACH', 'DOMESTIC_WIRE', 'CHEQUE', 'INTERNATIONAL_WIRE', 'BOOK_TRANSFER', 'STABLECOIN'])
    .notRequired(),
  amount: yup
    .object({
      amount: yup.number().notRequired(),
      currency: yup.string().nullable().notRequired(),
    })
    .notRequired(),
  process_date: yup.string().nullable().notRequired(),
  originating_account: yup
    .object({
      type: yup.string().notRequired(),
      id: yup.string().notRequired(),
    })
    .notRequired(),
  status: yup
    .string()
    .oneOf(['PROCESSING', 'SCHEDULED', 'PENDING_APPROVAL', 'FAILED', 'PROCESSED'])
    .notRequired(),
  cancellation_reason: yup
    .string()
    .oneOf(['USER_CANCELLED', 'INSUFFICIENT_FUNDS', 'APPROVAL_DENIED', 'BLOCKED_BY_POSITIVE_PAY'])
    .nullable()
    .notRequired(),
  estimated_delivery_date: yup.string().nullable().notRequired(),
  creator_user_id: yup.string().nullable().notRequired(),
  created_at: yup.string().nullable().notRequired(),
  display_name: yup.string().nullable().notRequired(),
  external_memo: yup.string().nullable().notRequired(),
  is_ppro_enabled: yup.boolean().nullable().notRequired(),
})

export type BrexGetTransferResponse = yup.InferType<typeof brexGetTransferResponseSchema>

// LINKED ACCOUNTS ---------------------------------------------------------------------------------

const brexBalanceSchema = yup.object({
  amount: yup
    .object({
      amount: yup.number().required(),
      currency: yup.string().nullable().notRequired(),
    })
    .required(),
  as_of_date: yup.string().required(),
})

export const brexLinkedAccountSchema = yup.object({
  id: yup.string().required(),
  bank_details: yup
    .object({
      name: yup.string().required(),
      type: yup.string().oneOf(['CHECKING', 'SAVING']).required(),
    })
    .required(),
  brex_account_id: yup.string().nullable().notRequired(),
  last_four: yup.string().required(),
  available_balance: brexBalanceSchema.nullable().notRequired(),
  current_balance: brexBalanceSchema.nullable().notRequired(),
})

export type BrexLinkedAccount = yup.InferType<typeof brexLinkedAccountSchema>

export const brexListLinkedAccountsResponseSchema = yup.object({
  next_cursor: yup.string().nullable().notRequired(),
  items: yup.array(brexLinkedAccountSchema).required(),
})

export type BrexListLinkedAccountsResponse = yup.InferType<
  typeof brexListLinkedAccountsResponseSchema
>

// LIST VENDORS ------------------------------------------------------------------------------------

/**
 * Flat combined schema for all vendor payment account detail types (ACH, DOMESTIC_WIRE, CHEQUE, INTERNATIONAL_WIRE).
 * The `type` field acts as a discriminator.
 *
 * All types: type (required), payment_instrument_id (required)
 * ACH + DOMESTIC_WIRE: routing_number, account_number
 * ACH only: account_type (CHECKING/SAVING), account_class (BUSINESS/PERSONAL)
 * DOMESTIC_WIRE + INTERNATIONAL_WIRE: address
 * CHEQUE only: mailing_address, recipient_name
 * INTERNATIONAL_WIRE only: swift_code, iban, beneficiary_bank_name
 */
const brexVendorPaymentAccountDetailsResponseSchema = yup.object({
  type: yup.string().oneOf(['ACH', 'DOMESTIC_WIRE', 'CHEQUE', 'INTERNATIONAL_WIRE']).required(),
  payment_instrument_id: yup.string().required(),
  // ACH + DOMESTIC_WIRE
  routing_number: yup.string().nullable().notRequired(),
  account_number: yup.string().nullable().notRequired(),
  // ACH only
  account_type: yup.string().oneOf(['CHECKING', 'SAVING']).nullable().notRequired(),
  account_class: yup.string().oneOf(['BUSINESS', 'PERSONAL']).nullable().notRequired(),
  // DOMESTIC_WIRE + INTERNATIONAL_WIRE
  address: brexBeneficiaryAddressSchema.nullable().notRequired(),
  // CHEQUE only
  mailing_address: brexBeneficiaryAddressSchema.nullable().notRequired(),
  recipient_name: yup.string().nullable().notRequired(),
  // INTERNATIONAL_WIRE only
  swift_code: yup.string().nullable().notRequired(),
  iban: yup.string().nullable().notRequired(),
  beneficiary_bank_name: yup.string().nullable().notRequired(),
})

const brexVendorPaymentAccountResponseSchema = yup.object({
  details: brexVendorPaymentAccountDetailsResponseSchema.required(),
})

export const brexVendorResponseItemSchema = yup.object({
  id: yup.string().required(),
  company_name: yup.string().nullable().notRequired(),
  email: yup.string().nullable().notRequired(),
  phone: yup.string().nullable().notRequired(),
  payment_accounts: yup.array(brexVendorPaymentAccountResponseSchema).nullable().notRequired(),
})

export type BrexVendorResponseItem = yup.InferType<typeof brexVendorResponseItemSchema>

export const brexListVendorsResponseSchema = yup.object({
  next_cursor: yup.string().nullable().notRequired(),
  items: yup.array(brexVendorResponseItemSchema).required(),
})

export type BrexListVendorsResponse = yup.InferType<typeof brexListVendorsResponseSchema>
